// Authentication Manager - Handles Firebase Auth (loaded via CDN ESM at runtime)
type User = any;

// Firebase configuration - will be injected from backend
declare global {
  interface Window {
    __FIREBASE_CONFIG__?: {
      apiKey: string;
      authDomain: string;
      projectId: string;
      storageBucket: string;
      messagingSenderId: string;
      appId: string;
    };
  }
}

const getFirebaseConfig = () => {
  // Try to get config from window (injected by backend)
  if (window.__FIREBASE_CONFIG__) {
    return window.__FIREBASE_CONFIG__;
  }

  // Fallback to placeholder config (will show error if used)
  return {
    apiKey: "your-api-key",
    authDomain: "your-project.firebaseapp.com",
    projectId: "your-project-id",
    storageBucket: "your-project.appspot.com",
    messagingSenderId: "123456789",
    appId: "your-app-id",
  };
};

export class AuthManager {
  private auth: any;
  private authMod: any;
  private currentUser: User | null = null;
  private listeners: ((user: User | null) => void)[] = [];

  constructor() {
    // Defer Firebase initialization to init()
  }

  async init(): Promise<void> {
    // Dynamically load Firebase ESM modules from CDN to avoid bundling
    // Load in parallel for better performance on mobile
    const firebaseAppUrl: string =
      "https://www.gstatic.com/firebasejs/10.13.1/firebase-app.js";
    const firebaseAuthUrl: string =
      "https://www.gstatic.com/firebasejs/10.13.1/firebase-auth.js";

    try {
      // Load both modules in parallel for faster initialization
      const [appMod, authMod] = await Promise.all([
        // @ts-ignore - CDN imports are resolved at runtime
        import(firebaseAppUrl),
        // @ts-ignore - CDN imports are resolved at runtime
        import(firebaseAuthUrl),
      ]);

      this.authMod = authMod;

      const firebaseConfig = getFirebaseConfig();

      // Validate config before initializing
      if (!firebaseConfig.apiKey || firebaseConfig.apiKey === "your-api-key") {
        console.error(
          "Firebase configuration is missing. Please configure Firebase Web App settings in your .env file."
        );
        throw new Error(
          "Firebase configuration not found. Please check your environment variables."
        );
      }

      const app = appMod.initializeApp(firebaseConfig);
      this.auth = this.authMod.getAuth(app);

      // Listen for auth state changes
      this.authMod.onAuthStateChanged(this.auth, (user: User) => {
        this.currentUser = user;
        this.notifyListeners(user);
      });
    } catch (error) {
      console.error("Failed to initialize Firebase:", error);
      // Don't throw - allow app to continue even if Firebase fails
      // This prevents blocking the entire app on slow networks
      console.warn("Firebase initialization failed, but app will continue");
    }
  }

  // Sign in with email and password
  async signIn(
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string; errorCode?: string }> {
    try {
      // Trim email to remove any whitespace
      const trimmedEmail = email.trim();

      await this.authMod.signInWithEmailAndPassword(
        this.auth,
        trimmedEmail,
        password
      );
      return { success: true };
    } catch (error: any) {
      // Log full error details for debugging
      // Firebase v9+ errors can have code in different places
      const errorCode =
        error?.code ||
        error?.errorCode ||
        error?.customData?.code ||
        (error?.message?.includes("auth/")
          ? error.message.match(/auth\/[a-z-]+/)?.[0]
          : null) ||
        "unknown";

      console.error("Sign in error details:", {
        code: errorCode,
        errorCode: error?.code,
        errorErrorCode: error?.errorCode,
        message: error?.message,
        fullError: error,
        errorType: typeof error,
        errorKeys: error ? Object.keys(error) : [],
        email: email, // Log email for debugging (password excluded for security)
      });

      const errorMessage = this.getErrorMessage(errorCode);

      // If we got an unexpected error code, log it
      if (errorCode === "unknown" || !errorCode) {
        console.warn("Unknown error code received. Full error:", error);
        // Try to extract auth error code from message if available
        if (error?.message) {
          const match = error.message.match(/auth\/([a-z-]+)/i);
          if (match) {
            const extractedCode = `auth/${match[1]}`;
            console.info("Extracted error code from message:", extractedCode);
            return {
              success: false,
              error: this.getErrorMessage(extractedCode),
              errorCode: extractedCode,
            };
          }
        }
      }

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
      };
    }
  }

  // Create account with email and password
  async createAccount(
    email: string,
    password: string,
    name: string,
    customerData?: {
      firstName: string;
      lastName: string;
      email: string;
      address: {
        line1: string;
        city: string;
        postal_code: string;
        country: string;
      };
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.authMod.createUserWithEmailAndPassword(
        this.auth,
        email.trim(), // Trim email to remove whitespace
        password
      );

      // Account created successfully

      // Note: We skip updating Firebase Auth displayName during registration because:
      // 1. The user object from CDN imports might not have updateProfile immediately available
      // 2. We store the full name in the backend customer record anyway (which is the source of truth)
      // The displayName can be updated later via account settings if needed

      // Create customer record in backend if customerData is provided
      if (customerData && result.user) {
        try {
          const token = await result.user.getIdToken();
          const response = await fetch("/api/account", {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              name: name,
              email: email.trim(),
              address: customerData.address,
            }),
          });

          if (!response.ok) {
            console.error(
              "Failed to create customer record:",
              response.statusText
            );
            // Don't fail registration if customer record creation fails
            // The user can update their profile later
          } else {
            // Customer record created successfully
          }
        } catch (error) {
          console.error("Error creating customer record:", error);
          // Don't fail registration if customer record creation fails
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error("Create account error:", {
        code: error?.code,
        message: error?.message,
        error: error,
        email: email,
      });
      return {
        success: false,
        error: this.getErrorMessage(error?.code || "unknown"),
      };
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await this.authMod.signOut(this.auth);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Check if Firebase auth is initialized
  isInitialized(): boolean {
    return this.auth !== null && this.auth !== undefined;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Get user ID token
  async getIdToken(): Promise<string | null> {
    if (!this.currentUser) return null;

    try {
      return await this.currentUser.getIdToken();
    } catch (error) {
      console.error("Failed to get ID token:", error);
      return null;
    }
  }

  // Add auth state listener
  onAuthStateChanged(listener: (user: User | null) => void): void {
    this.listeners.push(listener);
  }

  // Remove auth state listener
  removeAuthListener(listener: (user: User | null) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  // Notify listeners
  private notifyListeners(user: User | null): void {
    this.listeners.forEach((listener) => {
      try {
        listener(user);
      } catch (error) {
        console.error("Error in auth listener:", error);
      }
    });
  }

  // Get error message key from Firebase error code (returns translation key)
  getErrorMessageKey(errorCode: string): string {
    const errorKeys: Record<string, string> = {
      "auth/user-not-found": "auth.error.user_not_found",
      "auth/wrong-password": "auth.error.wrong_password",
      "auth/invalid-credential": "auth.error.invalid_credential", // Firebase v9+ uses this for both wrong password and user not found
      "auth/email-already-in-use": "auth.error.email_already_in_use",
      "auth/weak-password": "auth.error.weak_password",
      "auth/invalid-email": "auth.error.invalid_email",
      "auth/user-disabled": "auth.error.user_disabled",
      "auth/too-many-requests": "auth.error.too_many_requests",
      "auth/network-request-failed": "auth.error.network_error",
      "auth/configuration-not-found": "auth.error.configuration_not_found",
      "auth/operation-not-allowed": "auth.error.operation_not_allowed",
    };

    return errorKeys[errorCode] || "auth.error.generic";
  }

  // Get error message from Firebase error code (legacy - returns English text)
  private getErrorMessage(errorCode: string): string {
    const errorMessages: Record<string, string> = {
      "auth/user-not-found": "No account found with this email address.",
      "auth/wrong-password": "Incorrect password.",
      "auth/email-already-in-use": "An account with this email already exists.",
      "auth/weak-password": "Password should be at least 6 characters.",
      "auth/invalid-email": "Invalid email address.",
      "auth/user-disabled": "This account has been disabled.",
      "auth/too-many-requests":
        "Too many failed attempts. Please try again later.",
      "auth/network-request-failed":
        "Network error. Please check your connection.",
      "auth/invalid-credential":
        "Invalid email or password. Please check both and try again.",
      "auth/configuration-not-found":
        "Firebase Authentication is not configured. Please enable Email/Password authentication in Firebase Console > Authentication > Sign-in method.",
      "auth/operation-not-allowed":
        "This sign-in method is not enabled. Please enable Email/Password authentication in Firebase Console.",
    };

    return errorMessages[errorCode] || "An error occurred. Please try again.";
  }

  // Get user profile data
  getUserProfile(): {
    uid: string;
    email: string;
    name: string;
    photoURL?: string;
  } | null {
    if (!this.currentUser) return null;

    return {
      uid: this.currentUser.uid,
      email: this.currentUser.email || "",
      name: this.currentUser.displayName || "",
      photoURL: this.currentUser.photoURL || undefined,
    };
  }

  // Update user profile
  async updateProfile(updates: {
    displayName?: string;
    photoURL?: string;
  }): Promise<{ success: boolean; error?: string }> {
    if (!this.currentUser) {
      return { success: false, error: "No user logged in" };
    }

    try {
      await this.currentUser.updateProfile(updates);
      return { success: true };
    } catch (error: any) {
      console.error("Update profile error:", error);
      return {
        success: false,
        error: this.getErrorMessage(error.code),
      };
    }
  }

  // Send password reset email
  async sendPasswordResetEmail(
    email: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await this.authMod.sendPasswordResetEmail(this.auth, email);
      return { success: true };
    } catch (error: any) {
      console.error("Password reset error:", error);
      return {
        success: false,
        error: this.getErrorMessage(error.code),
      };
    }
  }

  // Delete user account
  async deleteAccount(): Promise<{ success: boolean; error?: string }> {
    if (!this.currentUser) {
      return { success: false, error: "No user logged in" };
    }

    try {
      await this.currentUser.delete();
      return { success: true };
    } catch (error: any) {
      console.error("Delete account error:", error);
      return {
        success: false,
        error: this.getErrorMessage(error.code),
      };
    }
  }
}
